<template>
  <Teleport to="body">
    <div class="chat-toast-container">
      <TransitionGroup name="toast-slide">
        <div
          v-for="toast in visibleToasts"
          :key="toast.id"
          class="chat-toast items-start"
          @click="handleToastClick(toast)"
        >
          <template v-if="toast.type === chatToastTypes.REACTION">
            <span class="chat-toast-emoji">{{ toast.emoji }}</span>
          </template>
          <template v-else>
            <ProfileIcon
              :data="{ name: toast.senderName, profile_pic: toast.senderAvatarUrl, avatar_color: toast.senderAvatarColor }"
              :size="28"
              :font="12"
            />
          </template>
          <div class="chat-toast-content">
            <div class="chat-toast-title primary_color">
              <template v-if="toast.type === chatToastTypes.REACTION">
                {{ toast.senderName }} reacted to your message
              </template>
              <template v-else-if="toast.type === chatToastTypes.MENTION">
                {{ toast.senderName }} mentioned you<template v-if="toast.groupName"> in #{{ toast.groupName }}</template>
              </template>
              <template v-else-if="toast.type === chatToastTypes.THREAD_REPLY">
                {{ toast.senderName }} replied to you
              </template>
              <template v-else>
                {{ toast.senderName }} sent you a message
              </template>
            </div>
            <div class="chat-toast-subtitle secondary_color">
              {{ toast.messagePreview }}
            </div>
            <div class="chat-toast-timestamp secondary_color">Just now</div>
          </div>
          <button
            class="chat-toast-close"
            @click.stop="dismissToast(toast.id)"
          >
            &times;
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import eventBus from "@/src/utils/eventBus.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { chatToastTypes } from "@/src/constants/chatConstants.js";

const router = useRouter();
const store = useStore();

const MAX_TOASTS = 5;
const AUTO_DISMISS_MS = 7000;

const toasts = ref([]);
const seenMessageIds = new Set();
let toastIdCounter = 0;

const visibleToasts = computed(() => toasts.value.slice(0, MAX_TOASTS));

const getBrowserNotificationTitle = (data) => {
  const sender = data.senderName || "Someone";
  const type = data.type || chatToastTypes.MESSAGE;
  if (type === chatToastTypes.REACTION) return `${sender} reacted ${data.emoji || ""}`.trim();
  if (type === chatToastTypes.MENTION) return `${sender} mentioned you`;
  if (type === chatToastTypes.THREAD_REPLY) return `${sender} replied to your thread`;
  return `${sender} sent a message`;
};

const showBrowserNotification = (data) => {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  if (document.hasFocus()) return;
  
  try {
    const title = getBrowserNotificationTitle(data);
    const body = data.messagePreview || "";
    const notification = new Notification(title, {
      body,
      icon: data.senderAvatarUrl || undefined,
      tag: data.messageId || undefined,
    });

    notification.onclick = () => {
      window.focus();
      const groups = store.getters["chats/groups"] || [];
      const group = groups.find((g) => {
        const gId = String(g.id).replace(/^group_/, "");
        const targetId = String(data.groupId).replace(/^group_/, "");
        return gId === targetId;
      });
      if (group) {
        store.commit("chats/SET_LAST_FETCHED_CHAT_ID", null);
        store.dispatch("chats/setActiveChat", group);
      }
      router.push({ name: "chats" });
      notification.close();
    };
  } catch (error) {
    console.error("Failed to show browser notification:", error);
  }
};

const addToast = (data) => {
  if (data.messageId && seenMessageIds.has(data.messageId)) return;
  if (data.messageId) seenMessageIds.add(data.messageId);

  showBrowserNotification(data);

  const id = ++toastIdCounter;
  const toast = {
    id,
    type: data.type || chatToastTypes.MESSAGE,
    messageId: data.messageId,
    groupId: data.groupId,
    senderName: data.senderName,
    senderAvatarUrl: data.senderAvatarUrl || null,
    senderAvatarColor: data.senderAvatarColor || null,
    emoji: data.emoji || null,
    messagePreview: data.messagePreview,
    groupName: data.groupName || null,
    threadRootId: data.threadRootId || null,
    timerId: null,
  };

  toast.timerId = setTimeout(() => {
    dismissToast(id);
  }, AUTO_DISMISS_MS);

  toasts.value.unshift(toast);

  if (toasts.value.length > MAX_TOASTS) {
    const removed = toasts.value.pop();
    if (removed.timerId) clearTimeout(removed.timerId);
    if (removed.messageId) seenMessageIds.delete(removed.messageId);
  }
};

const dismissToast = (id) => {
  const index = toasts.value.findIndex((t) => t.id === id);
  if (index !== -1) {
    const toast = toasts.value[index];
    if (toast.timerId) clearTimeout(toast.timerId);
    if (toast.messageId) seenMessageIds.delete(toast.messageId);
    toasts.value.splice(index, 1);
  }
};

const handleToastClick = (toast) => {
  const groups = store.getters["chats/groups"] || [];
  const group = groups.find((g) => {
    const gId = String(g.id).replace(/^group_/, "");
    const targetId = String(toast.groupId).replace(/^group_/, "");
    return gId === targetId;
  });

  if (group) {
    store.commit("chats/SET_LAST_FETCHED_CHAT_ID", null);
    store.dispatch("chats/setActiveChat", group);
  }

  router.push({ name: "chats" }).then(() => {
    if (toast.type === chatToastTypes.THREAD_REPLY && toast.threadRootId) {
      store.dispatch("chats/toggleThread", {
        forceState: true,
        messageID: toast.threadRootId,
        threadId: toast.threadRootId,
      });
    }
  }).catch((err) => {
    console.error("Toast navigation failed:", err);
  });
  dismissToast(toast.id);
};

onMounted(() => {
  eventBus.on("chat-toast-message", addToast);

  // if ("Notification" in window && Notification.permission === "default") {
  //   Notification.requestPermission();
  // }
});

onUnmounted(() => {
  eventBus.off("chat-toast-message", addToast);
  toasts.value.forEach((t) => {
    if (t.timerId) clearTimeout(t.timerId);
  });
});
</script>

<style scoped>
.chat-toast-container {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}

.chat-toast {
  width: 325px;
  padding: 12px;
  border-radius: 8px;
  background: var(--background-color);
  border: 1px solid var(--white-opacity-12, rgba(255, 255, 255, 0.12));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  pointer-events: auto;
}

.chat-toast-emoji {
  flex-shrink: 0;
  font-size: 22px;
  line-height: 28px;
  width: 28px;
  text-align: center;
}

.chat-toast-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chat-toast-title {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-toast-subtitle {
  font-size: 12px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-toast-timestamp {
  font-size: 11px;
  font-weight: 400;
}

.chat-toast-close {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--secondary-color);
  font-size: 16px;
  cursor: pointer;
  border-radius: 4px;
  line-height: 1;
  padding: 0;
}

.chat-toast-close:hover {
  background-color: var(--white-opacity-12, rgba(255, 255, 255, 0.12));
  color: var(--foreground-rgb);
}

.toast-slide-enter-active {
  transition: all 0.3s ease-out;
}

.toast-slide-leave-active {
  transition: all 0.3s ease-in;
}

.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-slide-move {
  transition: transform 0.3s ease;
}
</style>
